'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { validatePromoCode, validatePromoCodeWithUsage, checkGuestPromoUsage } from '@/services/promocode-api';
import { useMembership } from '@/contexts/MembershipContext';
import { getMemberLoginSession } from '@/services/memberLoginApi';
import { getPropertyCode } from '@/utils/propertyManager';

const PromoContext = createContext(null);

export const PromoProvider = ({ children }) => {
  const [activePromo, setActivePromo] = useState(null);
  const [isPromoMode, setIsPromoMode] = useState(false);
  const [promoDiscounts, setPromoDiscounts] = useState({});
  const [isLoadingPromo, setIsLoadingPromo] = useState(false);
  const [guestUsage, setGuestUsage] = useState(null); // Track guest usage info
  const { memberData, getCurrentUserEmail, hasPropertyMembership } = useMembership();

  // Get member identifier for usage tracking
  // Priority: member_code > member_id > email (OAuth session) — consistent with extractMemberId
  const getMemberIdentifier = useCallback(() => {
    // Priority 1: Get from memberData (MembershipContext)
    if (memberData) {
      return memberData.member_code || memberData.member_id || memberData.id || memberData.email || null;
    }
    
    // Priority 2: Get from manual login session
    const propertyCode = getPropertyCode();
    if (propertyCode) {
      const manualSession = getMemberLoginSession(propertyCode);
      if (manualSession?.memberData) {
        return manualSession.memberData.member_code || 
               manualSession.memberData.member_id ||
               manualSession.memberData.id || 
               manualSession.memberData.email || null;
      }
    }
    
    // Priority 3: Use email if logged in via OAuth (promo requires login with email)
    const email = getCurrentUserEmail?.();
    return email || null;
  }, [memberData, getCurrentUserEmail]);

  // Load promo from URL on mount
  useEffect(() => {
    const loadPromoFromUrl = async () => {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const promoCode = urlParams.get('promo_code');
        const promoData = urlParams.get('promo_data');
        
        // First priority: Load from promo_data if available
        if (promoData) {
          try {
            const parsed = JSON.parse(decodeURIComponent(promoData));
            setActivePromo(parsed);
            setIsPromoMode(true);
            console.log('✅ Loaded promo from URL promo_data:', parsed);
            
            // Check member usage if logged in
            const memberId = getMemberIdentifier();
            if (memberId && parsed.usage_per_guest > 0) {
              const usageCheck = await checkGuestPromoUsage(memberId, parsed);
              setGuestUsage(usageCheck);
              console.log('📊 Guest usage check for cached promo:', usageCheck);
              
              // If member exceeded usage, clear promo
              if (!usageCheck.canUse) {
                console.warn('⚠️ Member exceeded usage_per_guest limit, clearing promo');
                setActivePromo(null);
                setIsPromoMode(false);
                setGuestUsage(null);
              }
            }
            return;
          } catch (e) {
            console.error('Error parsing promo data from URL:', e);
          }
        }
        
        // Second priority: Validate promo_code via API if only promo_code is present
        if (promoCode && !promoData) {
          setIsLoadingPromo(true);
          try {
            console.log('🔄 Validating promo_code from URL:', promoCode);
            
            // Get member identifier for usage check
            const memberId = getMemberIdentifier();
            
            // Use enhanced validation with usage check
            const validation = memberId 
              ? await validatePromoCodeWithUsage(promoCode.trim(), memberId)
              : await validatePromoCode(promoCode.trim());
            
            if (validation.success && validation.data) {
              // Check if guest can use this promo
              if (validation.guestUsage && !validation.guestUsage.canUse) {
                console.warn('⚠️ Member exceeded usage_per_guest limit:', validation.guestUsage.message);
                // Don't set the promo
                setGuestUsage(validation.guestUsage);
                return;
              }
              
              setActivePromo(validation.data);
              setIsPromoMode(true);
              setGuestUsage(validation.guestUsage || null);
              console.log('✅ Loaded promo from API validation:', validation.data);
              console.log('📊 Guest usage info:', validation.guestUsage);
              
              // Update URL with promo_data for future use
              const url = new URL(window.location.href);
              url.searchParams.set('promo_data', encodeURIComponent(JSON.stringify({
                id: validation.data.id,
                promo_name: validation.data.promo_name,
                promo_code: validation.data.promo_code || validation.data.referral_code,
                discount_type: validation.data.discount_type,
                discount_value: validation.data.discount_value,
                max_discount: validation.data.max_discount,
                min_nights: validation.data.min_nights,
                max_nights: validation.data.max_nights,
                min_amount: validation.data.min_amount,
                usage_limit: validation.data.usage_limit,
                usage_per_guest: validation.data.usage_per_guest,
                room_types: validation.data.room_types,
                rate_plans: validation.data.rate_plans,
                room_type_ids: validation.data.room_type_ids,
                rate_plan_ids: validation.data.rate_plan_ids,
                total_usage: validation.data.total_usage,
                remaining_usage: validation.data.remaining_usage,
                booking_start: validation.data.booking_start,
                booking_end: validation.data.booking_end,
                stay_start: validation.data.stay_start,
                stay_end: validation.data.stay_end
              })));
              window.history.replaceState({}, '', url.toString());
            } else {
              console.warn('⚠️ Promo code validation failed:', validation.message);
              if (validation.guestUsage) {
                setGuestUsage(validation.guestUsage);
              }
            }
          } catch (error) {
            console.error('Error validating promo code from URL:', error);
          } finally {
            setIsLoadingPromo(false);
          }
        }
      }
    };
    
    loadPromoFromUrl();
  }, [getMemberIdentifier]);

  // Apply promo and update URL
  const applyPromo = useCallback(async (promo) => {
    console.log('=== APPLY PROMO CALLED ===');
    console.log('Promo data:', promo);
    console.log('Member ID:', getMemberIdentifier());
    
    // Check stacking: if there's already an active promo
    if (activePromo) {
      // If the existing promo is not stackable, reject
      if (activePromo.stackable === false || activePromo.stackable === 0) {
        console.warn('⚠️ Cannot stack: existing promo is not stackable');
        return { success: false, message: `Cannot combine with "${activePromo.promo_name}". This promo does not allow stacking with other offers.` };
      }
      // If the new promo is not stackable, reject
      if (promo.stackable === false || promo.stackable === 0) {
        console.warn('⚠️ Cannot stack: new promo is not stackable');
        return { success: false, message: `"${promo.promo_name}" cannot be combined with other promos. Please clear the current promo first.` };
      }
    }
    
    // Check member usage before applying
    const memberId = getMemberIdentifier();
    
    console.log('usage_per_guest value:', promo.usage_per_guest, 'type:', typeof promo.usage_per_guest);
    
    if (memberId && promo.usage_per_guest > 0) {
      console.log('Checking member usage...');
      const usageCheck = await checkGuestPromoUsage(memberId, promo);
      console.log('Usage check result:', usageCheck);
      setGuestUsage(usageCheck);
      
      if (!usageCheck.canUse) {
        console.warn('⚠️ Member exceeded usage_per_guest limit:', usageCheck.message);
        return { success: false, message: usageCheck.message };
      }
    }
    
    console.log('✅ Setting active promo...');
    setActivePromo(promo);
    setIsPromoMode(true);
    
    // Update URL with promo data
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('promo_code', promo.promo_code);
      url.searchParams.set('promo_data', encodeURIComponent(JSON.stringify({
        id: promo.id,
        promo_name: promo.promo_name,
        promo_code: promo.promo_code,
        discount_type: promo.discount_type,
        discount_value: promo.discount_value,
        max_discount: promo.max_discount,
        min_nights: promo.min_nights,
        max_nights: promo.max_nights,
        min_amount: promo.min_amount,
        usage_limit: promo.usage_limit,
        usage_per_guest: promo.usage_per_guest,
        stackable: promo.stackable,
        room_types: promo.room_types,
        rate_plans: promo.rate_plans,
        room_type_ids: promo.room_type_ids,
        rate_plan_ids: promo.rate_plan_ids,
        total_usage: promo.total_usage,
        remaining_usage: promo.remaining_usage,
        booking_start: promo.booking_start,
        booking_end: promo.booking_end,
        stay_start: promo.stay_start,
        stay_end: promo.stay_end
      })));

      window.history.pushState({}, '', url.toString());
    }
    
    console.log('Applied promo:', promo);
    return { success: true };
  }, [getMemberIdentifier, activePromo]);

  // Clear promo
  const clearPromo = useCallback(() => {
    setActivePromo(null);
    setIsPromoMode(false);
    setPromoDiscounts({});
    setGuestUsage(null);
    
    // Remove promo from URL
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('promo_code');
      url.searchParams.delete('promo_data');
      window.history.pushState({}, '', url.toString());
    }
  }, []);

  // Refresh guest usage check (call after booking to update usage count)
  const refreshGuestUsage = useCallback(async () => {
    if (!activePromo) return;
    
    const memberId = getMemberIdentifier();
    if (memberId) {
      const usageCheck = await checkGuestPromoUsage(memberId, activePromo);
      setGuestUsage(usageCheck);
      console.log('🔄 Refreshed guest usage:', usageCheck);
    }
  }, [activePromo, getMemberIdentifier]);

  // Check if a room is eligible for the promo
  const isRoomEligible = useCallback((room) => {
    if (!activePromo) return true;
    
    const roomType = room.room_info?.room_type;
    const rateCode = room.rate_details?.ratecode || room.rate_details?.rate_code;
    
    // If no restrictions, all rooms are eligible
    if ((!activePromo.room_types || activePromo.room_types.length === 0) &&
        (!activePromo.rate_plans || activePromo.rate_plans.length === 0)) {
      return true;
    }
    
    // Check if room type matches
    const roomTypeMatch = !activePromo.room_types || 
                         activePromo.room_types.length === 0 || 
                         activePromo.room_types.includes(roomType);
            
    // Check if rate plan matches (flexible matching)
    // The ratecode might be "BEST_DEAL_WB" but promo has "BEST_DEAL"
    const ratePlanMatch = !activePromo.rate_plans || 
                         activePromo.rate_plans.length === 0 ||
                         activePromo.rate_plans.some(rp =>
                           rateCode === rp ||
                           rateCode?.startsWith(rp) ||
                           rateCode?.includes(rp)
                         );
    
    // Both room type AND rate plan must match when restrictions are specified
    // If only room_types is specified, only room type needs to match
    // If only rate_plans is specified, only rate plan needs to match
    // If both are specified, BOTH must match
    const hasRoomTypeRestriction = activePromo.room_types && activePromo.room_types.length > 0;
    const hasRatePlanRestriction = activePromo.rate_plans && activePromo.rate_plans.length > 0;
        
    if (hasRoomTypeRestriction && hasRatePlanRestriction) {
      // Both restrictions exist - BOTH must match
      return roomTypeMatch && ratePlanMatch;
    } else if (hasRoomTypeRestriction) {
      // Only room type restriction
      return roomTypeMatch;
    } else if (hasRatePlanRestriction) {
      // Only rate plan restriction
      return ratePlanMatch;
    }
        
    // No restrictions
    return true;
  }, [activePromo]);

  // Calculate discount for a room
  const calculateDiscount = useCallback((room, nights = 1) => {
    if (!activePromo || !isRoomEligible(room)) {
      return { discount: 0, finalPrice: room.rate_details?.final_rate || 0, isPerNight: false };
    }
    
    const basePrice = room.rate_details?.final_rate || 0;
    const totalAmount = basePrice * nights;
    
    // Check minimum amount requirement
    if (activePromo.min_amount && totalAmount < activePromo.min_amount) {
      return { discount: 0, finalPrice: basePrice, isPerNight: false };
    }
    
    // Check minimum nights requirement
    if (activePromo.min_nights && nights < activePromo.min_nights) {
      return { discount: 0, finalPrice: basePrice, isPerNight: false };
    }
    
    let discount = 0;
    let isPerNight = true; // Default: discount is per-night (for percentage)
    let totalDiscount = 0; // Total discount for the entire stay
    
    switch (activePromo.discount_type) {
      case 'percentage':
        // Percentage is calculated per night
        discount = (basePrice * activePromo.discount_value) / 100;
        // Apply max discount if set (per night)
        if (activePromo.max_discount && discount > activePromo.max_discount) {
          discount = activePromo.max_discount;
        }
        totalDiscount = discount * nights;
        isPerNight = true;
        break;
        
      case 'fixed_amount':
        // Fixed amount is the TOTAL discount for the entire stay
        totalDiscount = activePromo.discount_value;
        // Cap total discount at total amount
        totalDiscount = Math.min(totalDiscount, totalAmount);
        // Calculate per-night discount for display
        discount = nights > 0 ? totalDiscount / nights : 0;
        isPerNight = false;
        break;
        
      case 'free_night':
        // Free night discount = price per night * number of free nights
        const freeNights = Math.min(activePromo.discount_value, nights);
        totalDiscount = basePrice * freeNights;
        discount = basePrice; // Per-night equivalent
        isPerNight = true;
        break;
        
      default:
        discount = 0;
        totalDiscount = 0;
        isPerNight = false;
    }
    
    // For percentage and free_night, cap per-night discount at base price
    if (isPerNight) {
      discount = Math.min(discount, basePrice);
    }
    
    // Calculate final price per night
    const finalPricePerNight = nights > 0 ? basePrice - (totalDiscount / nights) : basePrice;
    
    return {
      discount,              // Per-night discount (for display)
      totalDiscount,         // Total discount for entire stay
      finalPrice: finalPricePerNight,  // Price per night after discount
      discountType: activePromo.discount_type,
      discountValue: activePromo.discount_value,
      maxDiscount: activePromo.max_discount,
      isPerNight,            // Whether discount is per-night or total
      nights                 // Number of nights for reference
    };
  }, [activePromo, isRoomEligible]);

  // Format discount text for display
  const formatDiscountText = useCallback(() => {
    if (!activePromo) return '';
    
    switch (activePromo.discount_type) {
      case 'percentage':
        return `${activePromo.discount_value}% OFF`;
      case 'fixed_amount':
        return `Rp ${new Intl.NumberFormat('id-ID').format(activePromo.discount_value)} OFF`;
      case 'free_night':
        return `${activePromo.discount_value} FREE NIGHT${activePromo.discount_value > 1 ? 'S' : ''}`;
      default:
        return 'Special Offer';
    }
  }, [activePromo]);

  const value = {
    activePromo,
    isPromoMode,
    promoDiscounts,
    guestUsage, // Expose guest usage info
    applyPromo,
    clearPromo,
    isRoomEligible,
    calculateDiscount,
    formatDiscountText,
    refreshGuestUsage, // Expose refresh function
    getMemberIdentifier // Expose for external use
  };

  return (
    <PromoContext.Provider value={value}>
      {children}
    </PromoContext.Provider>
  );
};

export const usePromo = () => {
  const context = useContext(PromoContext);
  if (!context) {
    throw new Error('usePromo must be used within a PromoProvider');
  }
  return context;
};

export default PromoContext;
